import type { MerchantConfig, MerchantId, ExtraSource, SourceConfig } from "./types";

// Re-export types and utilities
export * from "./types";
export * from "./selectors";
export { validateAllMerchants, validateMerchantConfig } from "./schema";
export { 
  CATEGORY_LABELS, 
  ALL_CATEGORIES, 
  getCategoryLabel,
  type CategoryKey 
} from "../categories";

// =============================================================================
// HELPER: Create source config
// =============================================================================
function src(
  merchantId: MerchantId,
  id: string,
  label: string,
  url: string,
  sourceType: SourceConfig["sourceType"],
  opts: Partial<SourceConfig> = {}
): SourceConfig {
  return {
    id,
    merchantId,
    label,
    url,
    sourceType,
    scope: opts.scope ?? "ON",
    official: opts.official ?? true,
    priority: opts.priority ?? 10,
    parserHint: opts.parserHint,
    notes: opts.notes,
  };
}

// =============================================================================
// MERCHANTS - Single Source of Truth (7 Categories)
// =============================================================================
// To add a merchant: Add the id to MerchantId union in types.ts, then add config here.
// To add a source: Add to the sources[] array for that merchant.
// =============================================================================

export const MERCHANTS: Record<MerchantId, MerchantConfig> = {
  // ========== ELECTRONICS ==========
  "apple": {
    id: "apple",
    displayName: "Apple",
    categoryKey: "Electronics",
    merchantDomainsKey: "apple",
    sources: [],
  },
  "apple-music": {
    id: "apple-music",
    displayName: "Apple Music",
    categoryKey: "Electronics",
    merchantDomainsKey: "apple-music",
    sources: [],
  },
  "best-buy": {
    id: "best-buy",
    displayName: "Best Buy",
    categoryKey: "Electronics",
    merchantDomainsKey: "best-buy",
    sources: [
      src("best-buy", "bestbuy-gc", "Best Buy Gift Cards", "https://www.bestbuy.ca/en-ca/collection/gift-cards/349917", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "cineplex": {
    id: "cineplex",
    displayName: "Cineplex",
    categoryKey: "Electronics",
    merchantDomainsKey: "cineplex",
    sources: [
      src("cineplex", "cineplex-gc", "Cineplex Gift Cards", "https://www.cineplex.com/giftcards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "disney-plus": {
    id: "disney-plus",
    displayName: "Disney+",
    categoryKey: "Electronics",
    merchantDomainsKey: "disney-plus",
    sources: [
      src("disney-plus", "disney-gc", "Disney+ Gift Cards", "https://www.disneyplus.com/en-ca/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "landmark-cinemas": {
    id: "landmark-cinemas",
    displayName: "Landmark Cinemas",
    categoryKey: "Electronics",
    merchantDomainsKey: "landmark-cinemas",
    sources: [
      src("landmark-cinemas", "landmark-gc", "Landmark Cinemas Gift Cards", "https://www.landmarkcinemas.com/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "google-play": {
    id: "google-play",
    displayName: "Google Play",
    categoryKey: "Electronics",
    merchantDomainsKey: "google-play",
    sources: [],
  },
  "microsoft-xbox": {
    id: "microsoft-xbox",
    displayName: "Microsoft / Xbox",
    categoryKey: "Electronics",
    merchantDomainsKey: "microsoft-xbox",
    sources: [
      src("microsoft-xbox", "xbox-gc", "Xbox Gift Cards", "https://www.xbox.com/en-CA/gifts/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "netflix": {
    id: "netflix",
    displayName: "Netflix",
    categoryKey: "Electronics",
    merchantDomainsKey: "netflix",
    sources: [],
  },
  "nintendo": {
    id: "nintendo",
    displayName: "Nintendo",
    categoryKey: "Electronics",
    merchantDomainsKey: "nintendo",
    sources: [
      src("nintendo", "nintendo-gc", "Nintendo eShop Cards", "https://www.nintendo.com/en-ca/store/products/nintendo-eshop-card/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "playstation": {
    id: "playstation",
    displayName: "PlayStation",
    categoryKey: "Electronics",
    merchantDomainsKey: "playstation",
    sources: [
      src("playstation", "psn-gc", "PlayStation Store Gift Cards", "https://store.playstation.com/en-ca/category/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "razer-gold": {
    id: "razer-gold",
    displayName: "Razer Gold",
    categoryKey: "Electronics",
    merchantDomainsKey: "razer-gold",
    sources: [],
  },
  "roblox": {
    id: "roblox",
    displayName: "Roblox",
    categoryKey: "Electronics",
    merchantDomainsKey: "roblox",
    sources: [],
  },
  "spotify": {
    id: "spotify",
    displayName: "Spotify",
    categoryKey: "Electronics",
    merchantDomainsKey: "spotify",
    sources: [],
  },
  "staples": {
    id: "staples",
    displayName: "Staples",
    categoryKey: "Electronics",
    merchantDomainsKey: "staples",
    sources: [
      src("staples", "staples-gc", "Staples Gift Cards", "https://www.staples.ca/collections/gift-cards-1408", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "steam": {
    id: "steam",
    displayName: "Steam",
    categoryKey: "Electronics",
    merchantDomainsKey: "steam",
    sources: [],
  },
  "the-source": {
    id: "the-source",
    displayName: "The Source",
    categoryKey: "Electronics",
    merchantDomainsKey: "the-source",
    sources: [],
  },

  // ========== GROCERY ==========
  "farm-boy": {
    id: "farm-boy",
    displayName: "Farm Boy",
    categoryKey: "Grocery",
    merchantDomainsKey: "farm-boy",
    sources: [
      src("farm-boy", "farmboy-gc", "Farm Boy Gift Cards", "https://www.farmboy.ca/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "food-basics": {
    id: "food-basics",
    displayName: "Food Basics",
    categoryKey: "Grocery",
    merchantDomainsKey: "food-basics",
    sources: [],
  },
  "foodland": {
    id: "foodland",
    displayName: "Foodland",
    categoryKey: "Grocery",
    merchantDomainsKey: "foodland",
    sources: [],
  },
  "fortinos": {
    id: "fortinos",
    displayName: "Fortinos",
    categoryKey: "Grocery",
    merchantDomainsKey: "fortinos",
    sources: [],
  },
  "freshco": {
    id: "freshco",
    displayName: "FreshCo",
    categoryKey: "Grocery",
    merchantDomainsKey: "freshco",
    sources: [],
  },
  "independent-grocer": {
    id: "independent-grocer",
    displayName: "Independent Grocer",
    categoryKey: "Grocery",
    merchantDomainsKey: "independent-grocer",
    sources: [
      src("independent-grocer", "yig-gc", "Your Independent Grocer Gift Cards", "https://www.yourindependentgrocer.ca/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "loblaws": {
    id: "loblaws",
    displayName: "Loblaws",
    categoryKey: "Grocery",
    merchantDomainsKey: "loblaws",
    sources: [
      src("loblaws", "loblaws-terms", "Loblaws Gift Card FAQ", "https://www.giftofchoice.ca/en/faq/our-loblaw-family-of-gift-cards/purchasing-a-gift-card/where-can-i-purchase-gift-cards/", "TERMS_PAGE", { priority: 50, scope: "CA" }),
    ],
  },
  "longos": {
    id: "longos",
    displayName: "Longo's",
    categoryKey: "Grocery",
    merchantDomainsKey: "longos",
    sources: [
      src("longos", "longos-gc", "Longo's Gift Cards", "https://www.longos.com/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "metro": {
    id: "metro",
    displayName: "Metro",
    categoryKey: "Grocery",
    merchantDomainsKey: "metro",
    sources: [
      src("metro", "metro-flyer", "Metro Weekly Flyer", "https://www.metro.ca/en/flyer", "FLYER_LANDING", { priority: 1, parserHint: "jsRequired" }),
      src("metro", "metro-gc", "Metro Gift Card Page", "https://www.metro.ca/en/gift-card", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "no-frills": {
    id: "no-frills",
    displayName: "No Frills",
    categoryKey: "Grocery",
    merchantDomainsKey: "no-frills",
    sources: [],
  },
  "real-canadian-superstore": {
    id: "real-canadian-superstore",
    displayName: "Real Canadian Superstore",
    categoryKey: "Grocery",
    merchantDomainsKey: "real-canadian-superstore",
    sources: [],
  },
  "rexall": {
    id: "rexall",
    displayName: "Rexall",
    categoryKey: "Grocery",
    merchantDomainsKey: "rexall",
    sources: [
      src("rexall", "rexall-flyer", "Rexall eFlyer", "https://www.rexall.ca/eflyer", "FLYER_LANDING", { priority: 1, parserHint: "jsRequired" }),
    ],
  },
  "shoppers-drug-mart": {
    id: "shoppers-drug-mart",
    displayName: "Shoppers Drug Mart",
    categoryKey: "Grocery",
    merchantDomainsKey: "shoppers-drug-mart",
    sources: [
      src("shoppers-drug-mart", "sdm-flyer", "Shoppers Weekly Flyer", "https://www1.shoppersdrugmart.ca/en/flyer", "FLYER_LANDING", { priority: 1, parserHint: "jsRequired" }),
      src("shoppers-drug-mart", "sdm-terms", "Shoppers Gift Card Terms", "https://dis-prod.assetful.loblaw.ca/content/dam/loblaw-companies-limited/creative-assets/shoppers-drug-mart/corporate/documents/Gift%20cards%20-%20Detailed%20TandCs_EN.pdf", "TERMS_PAGE", { priority: 50, parserHint: "pdf", scope: "CA" }),
    ],
  },
  "sobeys": {
    id: "sobeys",
    displayName: "Sobeys",
    categoryKey: "Grocery",
    merchantDomainsKey: "sobeys",
    sources: [
      src("sobeys", "sobeys-flyer", "Sobeys Weekly Flyer", "https://www.sobeys.com/en/flyer/", "FLYER_LANDING", { priority: 1 }),
    ],
  },
  "valu-mart": {
    id: "valu-mart",
    displayName: "Valu-mart",
    categoryKey: "Grocery",
    merchantDomainsKey: "valu-mart",
    sources: [
      src("valu-mart", "valumart-gc", "Valu-mart Gift Cards", "https://www.valumart.ca/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "zehrs": {
    id: "zehrs",
    displayName: "Zehrs",
    categoryKey: "Grocery",
    merchantDomainsKey: "zehrs",
    sources: [],
  },

  // ========== HOME IMPROVEMENT ==========
  "home-depot": {
    id: "home-depot",
    displayName: "Home Depot",
    categoryKey: "HomeImprovement",
    merchantDomainsKey: "home-depot",
    sources: [
      src("home-depot", "homedepot-gc", "Home Depot Gift Cards", "https://www.homedepot.ca/en/home/customer-support/gift-cards.html", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "home-hardware": {
    id: "home-hardware",
    displayName: "Home Hardware",
    categoryKey: "HomeImprovement",
    merchantDomainsKey: "home-hardware",
    sources: [
      src("home-hardware", "homehardware-gc", "Home Hardware Gift Cards", "https://www.homehardware.ca/en/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "ikea": {
    id: "ikea",
    displayName: "IKEA",
    categoryKey: "HomeImprovement",
    merchantDomainsKey: "ikea",
    sources: [
      src("ikea", "ikea-gc", "IKEA Gift Cards", "https://www.ikea.com/ca/en/customer-service/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "rona": {
    id: "rona",
    displayName: "RONA",
    categoryKey: "HomeImprovement",
    merchantDomainsKey: "rona",
    sources: [
      src("rona", "rona-gc", "RONA Gift Cards", "https://www.rona.ca/en/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },

  // ========== RESTAURANTS ==========
  "a-and-w": {
    id: "a-and-w",
    displayName: "A&W",
    categoryKey: "Restaurants",
    merchantDomainsKey: "a-and-w",
    sources: [
      src("a-and-w", "aw-gc", "A&W Gift Cards", "https://web.aw.ca/en/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "boston-pizza": {
    id: "boston-pizza",
    displayName: "Boston Pizza",
    categoryKey: "Restaurants",
    merchantDomainsKey: "boston-pizza",
    sources: [
      src("boston-pizza", "bp-gc", "Boston Pizza Gift Cards", "https://bostonpizza.com/en/gift-cards.html", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "burger-king": {
    id: "burger-king",
    displayName: "Burger King",
    categoryKey: "Restaurants",
    merchantDomainsKey: "burger-king",
    sources: [],
  },
  "doordash": {
    id: "doordash",
    displayName: "DoorDash",
    categoryKey: "Restaurants",
    merchantDomainsKey: "doordash",
    sources: [],
  },
  "earls": {
    id: "earls",
    displayName: "Earls",
    categoryKey: "Restaurants",
    merchantDomainsKey: "earls",
    sources: [
      src("earls", "earls-corp-gc", "Earls Corporate Gift Cards", "https://earls.ca/blog/corporate-gifting-that-connects-get-up-to-20-back-on-orders-500/", "GIFT_CARD_PAGE", { priority: 30, notes: "Tiered corporate discount: $500+=10%, $2500+=12%, $5000+=15%, $10000+=18%, $15000+=20% back" }),
      src("earls", "earls-gc", "Earls Gift Cards", "https://earls.ca/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "freshii": {
    id: "freshii",
    displayName: "Freshii",
    categoryKey: "Restaurants",
    merchantDomainsKey: "freshii",
    sources: [
      src("freshii", "freshii-gc", "Freshii Gift Cards", "https://www.freshii.com/ca/en-ca/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "milestones": {
    id: "milestones",
    displayName: "Milestones",
    categoryKey: "Restaurants",
    merchantDomainsKey: "milestones",
    sources: [
      src("milestones", "milestones-gc", "Milestones Gift Cards", "https://www.milestonesrestaurants.com/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "pita-pit": {
    id: "pita-pit",
    displayName: "Pita Pit",
    categoryKey: "Restaurants",
    merchantDomainsKey: "pita-pit",
    sources: [
      src("pita-pit", "pitapit-gc", "Pita Pit Gift Cards", "https://pitapit.ca/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "quesada": {
    id: "quesada",
    displayName: "Quesada",
    categoryKey: "Restaurants",
    merchantDomainsKey: "quesada",
    sources: [
      src("quesada", "quesada-gc", "Quesada Gift Cards", "https://www.quesada.ca/gift-cards/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "red-lobster": {
    id: "red-lobster",
    displayName: "Red Lobster",
    categoryKey: "Restaurants",
    merchantDomainsKey: "red-lobster",
    sources: [
      src("red-lobster", "redlobster-gc", "Red Lobster Gift Cards", "https://www.redlobster.ca/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "skip-the-dishes": {
    id: "skip-the-dishes",
    displayName: "SkipTheDishes",
    categoryKey: "Restaurants",
    merchantDomainsKey: "skip-the-dishes",
    sources: [
      src("skip-the-dishes", "skip-gc", "SkipTheDishes Gift Cards", "https://www.skipthedishes.com/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "starbucks": {
    id: "starbucks",
    displayName: "Starbucks",
    categoryKey: "Restaurants",
    merchantDomainsKey: "starbucks",
    sources: [],
  },
  "subway": {
    id: "subway",
    displayName: "Subway",
    categoryKey: "Restaurants",
    merchantDomainsKey: "subway",
    sources: [
      src("subway", "subway-gc", "Subway Gift Cards", "https://www.subway.com/en-CA/SubwayCard/GiftCards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "the-keg": {
    id: "the-keg",
    displayName: "The Keg",
    categoryKey: "Restaurants",
    merchantDomainsKey: "the-keg",
    sources: [
      src("the-keg", "thekeg-gc", "The Keg Gift Cards", "https://www.thekeg.com/gift-cards", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "tim-hortons": {
    id: "tim-hortons",
    displayName: "Tim Hortons",
    categoryKey: "Restaurants",
    merchantDomainsKey: "tim-hortons",
    sources: [],
  },

  // ========== SHOPPING ==========
  "adidas": {
    id: "adidas",
    displayName: "Adidas",
    categoryKey: "Shopping",
    merchantDomainsKey: "adidas",
    sources: [],
  },
  "american-eagle": {
    id: "american-eagle",
    displayName: "American Eagle",
    categoryKey: "Shopping",
    merchantDomainsKey: "american-eagle",
    sources: [],
  },
  "ardene": {
    id: "ardene",
    displayName: "Ardene",
    categoryKey: "Shopping",
    merchantDomainsKey: "ardene",
    sources: [],
  },
  "athleta": {
    id: "athleta",
    displayName: "Athleta",
    categoryKey: "Shopping",
    merchantDomainsKey: "athleta",
    sources: [],
  },
  "banana-republic": {
    id: "banana-republic",
    displayName: "Banana Republic",
    categoryKey: "Shopping",
    merchantDomainsKey: "banana-republic",
    sources: [],
  },
  "canadian-tire": {
    id: "canadian-tire",
    displayName: "Canadian Tire",
    categoryKey: "Shopping",
    merchantDomainsKey: "canadian-tire",
    sources: [
      src("canadian-tire", "ct-flyer", "Canadian Tire Weekly Flyer", "https://www.canadiantire.ca/en/flyer.html", "FLYER_LANDING", { priority: 1 }),
      src("canadian-tire", "ct-gc", "Canadian Tire Gift Cards", "https://giftcards.canadiantire.ca/", "GIFT_CARD_PAGE", { priority: 20 }),
    ],
  },
  "costco": {
    id: "costco",
    displayName: "Costco",
    categoryKey: "Shopping",
    merchantDomainsKey: "costco",
    sources: [],
  },
  "dollarama": {
    id: "dollarama",
    displayName: "Dollarama",
    categoryKey: "Shopping",
    merchantDomainsKey: "dollarama",
    sources: [],
  },
  "foot-locker": {
    id: "foot-locker",
    displayName: "Foot Locker",
    categoryKey: "Shopping",
    merchantDomainsKey: "foot-locker",
    sources: [],
  },
  "gap": {
    id: "gap",
    displayName: "Gap",
    categoryKey: "Shopping",
    merchantDomainsKey: "gap",
    sources: [],
  },
  "giant-tiger": {
    id: "giant-tiger",
    displayName: "Giant Tiger",
    categoryKey: "Shopping",
    merchantDomainsKey: "giant-tiger",
    sources: [],
  },
  "h-and-m": {
    id: "h-and-m",
    displayName: "H&M",
    categoryKey: "Shopping",
    merchantDomainsKey: "h-and-m",
    sources: [],
  },
  "homesense": {
    id: "homesense",
    displayName: "HomeSense",
    categoryKey: "Shopping",
    merchantDomainsKey: "homesense",
    sources: [],
  },
  "indigo": {
    id: "indigo",
    displayName: "Indigo",
    categoryKey: "Shopping",
    merchantDomainsKey: "indigo",
    sources: [],
  },
  "marshalls": {
    id: "marshalls",
    displayName: "Marshalls",
    categoryKey: "Shopping",
    merchantDomainsKey: "marshalls",
    sources: [],
  },
  "marks": {
    id: "marks",
    displayName: "Mark's",
    categoryKey: "Shopping",
    merchantDomainsKey: "marks",
    sources: [],
  },
  "old-navy": {
    id: "old-navy",
    displayName: "Old Navy",
    categoryKey: "Shopping",
    merchantDomainsKey: "old-navy",
    sources: [],
  },
  "petsmart": {
    id: "petsmart",
    displayName: "PetSmart",
    categoryKey: "Shopping",
    merchantDomainsKey: "petsmart",
    sources: [
      src("petsmart", "petsmart-gc", "PetSmart Canada Gift Cards", "https://www.petsmart.ca/gift-cards", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("petsmart", "petsmart-terms", "PetSmart CA Gift Card Terms", "https://www.petsmart.ca/help/ca-giftcard-terms-and-conditions.html", "TERMS_PAGE", { priority: 2, scope: "CA" }),
      src("petsmart", "petsmart-help", "PetSmart Gift Services Help", "https://www.petsmart.ca/help/gift-services-H0005.html", "TERMS_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "pro-hockey-life": {
    id: "pro-hockey-life",
    displayName: "Pro Hockey Life",
    categoryKey: "Shopping",
    merchantDomainsKey: "pro-hockey-life",
    sources: [],
  },
  "roots": {
    id: "roots",
    displayName: "Roots",
    categoryKey: "Shopping",
    merchantDomainsKey: "roots",
    sources: [],
  },
  "saks-off-5th": {
    id: "saks-off-5th",
    displayName: "Saks OFF 5th",
    categoryKey: "Shopping",
    merchantDomainsKey: "saks-off-5th",
    sources: [],
  },
  "sport-chek": {
    id: "sport-chek",
    displayName: "Sport Chek",
    categoryKey: "Shopping",
    merchantDomainsKey: "sport-chek",
    sources: [
      src("sport-chek", "sportchek-terms", "Sport Chek Gift Card FAQ", "https://www.sportchek.ca/en/customer-service/help-desk/gift-card/gift-card-faqs.html", "TERMS_PAGE", { priority: 30, scope: "CA" }),
    ],
  },
  "toys-r-us": {
    id: "toys-r-us",
    displayName: 'Toys "R" Us',
    categoryKey: "Shopping",
    merchantDomainsKey: "toys-r-us",
    sources: [],
  },
  "vaughan-mills": {
    id: "vaughan-mills",
    displayName: "Vaughan Mills",
    categoryKey: "Shopping",
    merchantDomainsKey: "vaughan-mills",
    sources: [
      src("vaughan-mills", "vm-portal", "Vaughan Mills Gift Card Portal", "https://giftcard.vaughanmills.com/", "GIFT_CARD_PAGE", { priority: 1 }),
      src("vaughan-mills", "vm-services", "Vaughan Mills Services Page", "https://www.vaughanmills.com/services/", "PROMO_PAGE", { priority: 2 }),
    ],
  },
  "walmart": {
    id: "walmart",
    displayName: "Walmart",
    categoryKey: "Shopping",
    merchantDomainsKey: "walmart",
    sources: [
      src("walmart", "walmart-flyer", "Walmart Weekly Flyer", "https://www.walmart.ca/flyer", "FLYER_LANDING", { priority: 1 }),
      src("walmart", "getmybonus-portal", "GetMyBonus Portal", "https://getmybonus.ca/", "BONUS_PORTAL", { priority: 5, scope: "CA" }),
    ],
  },
  "winners": {
    id: "winners",
    displayName: "Winners",
    categoryKey: "Shopping",
    merchantDomainsKey: "winners",
    sources: [],
  },
  "lululemon": {
    id: "lululemon",
    displayName: "lululemon",
    categoryKey: "Shopping",
    merchantDomainsKey: "lululemon",
    sources: [],
  },

  // ========== SPECIALTY ==========
  "bath-and-body-works": {
    id: "bath-and-body-works",
    displayName: "Bath & Body Works",
    categoryKey: "Specialty",
    merchantDomainsKey: "bath-and-body-works",
    sources: [
      src("bath-and-body-works", "bbw-gc-ca", "B&BW Canada Gift Cards", "https://www.bathandbodyworks.ca/en/giftcards", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("bath-and-body-works", "bbw-gc-us", "B&BW US Gift Cards (fallback)", "https://www.bathandbodyworks.com/giftcards", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
    ],
  },
  "one4all": {
    id: "one4all",
    displayName: "One4all",
    categoryKey: "Specialty",
    merchantDomainsKey: "one4all",
    sources: [
      src("one4all", "one4all-home", "One4all Canada Home", "https://www.one4allcard.ca/", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
    ],
  },
  "sephora": {
    id: "sephora",
    displayName: "Sephora",
    categoryKey: "Specialty",
    merchantDomainsKey: "sephora",
    sources: [],
  },
  "ultimate-choice": {
    id: "ultimate-choice",
    displayName: "Ultimate Choice",
    categoryKey: "Specialty",
    merchantDomainsKey: "ultimate-choice",
    sources: [],
  },
  "vanilla-visa": {
    id: "vanilla-visa",
    displayName: "Vanilla Visa",
    categoryKey: "Specialty",
    merchantDomainsKey: "vanilla-visa",
    sources: [],
  },
  "wayspa": {
    id: "wayspa",
    displayName: "WaySpa",
    categoryKey: "Specialty",
    merchantDomainsKey: "wayspa",
    sources: [],
  },

  // ========== TRAVEL ==========
  "air-canada": {
    id: "air-canada",
    displayName: "Air Canada",
    categoryKey: "Travel",
    merchantDomainsKey: "air-canada",
    sources: [
      src("air-canada", "ac-gc", "Air Canada Gift Cards", "https://www.aircanada.com/ca/en/aco/home/book/payment-methods/air-canada-gift-cards.html", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("air-canada", "ac-buyatab", "Air Canada Gift Card Portal (Buyatab)", "https://aircanada.buyatab.com/custom/aircanada/", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
      src("air-canada", "ac-balance", "Air Canada Gift Card Balance Check", "https://aircanada.buyatab.com/custom/aircanada/balance.aspx", "TERMS_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "airbnb": {
    id: "airbnb",
    displayName: "Airbnb",
    categoryKey: "Travel",
    merchantDomainsKey: "airbnb",
    sources: [],
  },
  "esso": {
    id: "esso",
    displayName: "Esso",
    categoryKey: "Travel",
    merchantDomainsKey: "esso",
    sources: [
      src("esso", "esso-gc", "Esso Gas Gift Cards", "https://www.esso.ca/en-ca/gas-gift-cards", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("esso", "esso-portal", "Esso Gift Card Portal", "https://giftcards.esso.ca/", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
      src("esso", "esso-balance", "Esso Gift Card Balance", "https://giftcards.esso.ca/balance", "TERMS_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "mobil": {
    id: "mobil",
    displayName: "Mobil",
    categoryKey: "Travel",
    merchantDomainsKey: "mobil",
    sources: [
      src("mobil", "mobil-gc", "Esso/Mobil Gas Gift Cards", "https://www.esso.ca/en-ca/gas-gift-cards", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("mobil", "mobil-portal", "Esso/Mobil Gift Card Portal", "https://giftcards.esso.ca/", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
      src("mobil", "mobil-balance", "Esso/Mobil Gift Card Balance", "https://giftcards.esso.ca/balance", "TERMS_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "petro-canada": {
    id: "petro-canada",
    displayName: "Petro-Canada",
    categoryKey: "Travel",
    merchantDomainsKey: "petro-canada",
    sources: [],
  },
  "shell": {
    id: "shell",
    displayName: "Shell",
    categoryKey: "Travel",
    merchantDomainsKey: "shell",
    sources: [
      src("shell", "shell-gc", "Shell Canada Gift Cards", "https://www.shell.ca/en_ca/drivers/loyalty-and-payment/gift-card.html", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("shell", "shell-portal", "Shell Gift Card Portal", "https://shellgiftcard.ca/", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
      src("shell", "shell-balance", "Shell Gift Card Balance Inquiry", "https://shellgiftcard.ca/order/BalanceInquiry", "TERMS_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "uber": {
    id: "uber",
    displayName: "Uber",
    categoryKey: "Travel",
    merchantDomainsKey: "uber",
    sources: [],
  },
  "uber-eats": {
    id: "uber-eats",
    displayName: "Uber Eats",
    categoryKey: "Travel",
    merchantDomainsKey: "uber-eats",
    sources: [
      src("uber-eats", "ue-gc-ca", "Uber Eats Gift Card (CA)", "https://about.ubereats.com/ca/en/how-it-works/uber-eats-gift-card/", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("uber-eats", "ue-gc-info", "Uber Gift Cards Info (CA)", "https://www.uber.com/ca/en/gift-cards/info/", "GIFT_CARD_PAGE", { priority: 2, scope: "CA" }),
      src("uber-eats", "ue-gc-store", "Uber Gift Cards Storefront", "https://gifts.uber.com/", "GIFT_CARD_PAGE", { priority: 3, scope: "CA" }),
    ],
  },
  "ultramar": {
    id: "ultramar",
    displayName: "Ultramar",
    categoryKey: "Travel",
    merchantDomainsKey: "ultramar",
    sources: [],
  },
  "via-rail": {
    id: "via-rail",
    displayName: "VIA Rail",
    categoryKey: "Travel",
    merchantDomainsKey: "via-rail",
    sources: [
      src("via-rail", "vr-gc", "VIA Rail Gift Cards", "https://www.viarail.ca/en/offers/gift-cards", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("via-rail", "vr-terms", "VIA Rail Gift Card Terms", "https://www.viarail.ca/en/offers/gift-cards/terms-conditions", "TERMS_PAGE", { priority: 2, scope: "CA" }),
    ],
  },
  "westjet": {
    id: "westjet",
    displayName: "WestJet",
    categoryKey: "Travel",
    merchantDomainsKey: "westjet",
    sources: [
      src("westjet", "wj-gc", "WestJet Gift Cards", "https://www.westjet.com/en-ca/extras/gift-card", "GIFT_CARD_PAGE", { priority: 1, scope: "CA" }),
      src("westjet", "wj-help", "WestJet Gift Card Help", "https://www.westjet.com/en-ca/help/gift-card", "TERMS_PAGE", { priority: 2, scope: "CA" }),
    ],
  },
};

// =============================================================================
// EXTRA SOURCES - Forums and Aggregators (locator-only, not for proof)
// =============================================================================

export const EXTRA_SOURCES: ExtraSource[] = [
  {
    name: "RedFlagDeals Gift Card Forum",
    url: "https://forums.redflagdeals.com/tags/gift%20card/",
    type: "forum",
    priority: 1,
  },
  {
    name: "GetMyBonus Portal",
    url: "https://getmybonus.ca/",
    type: "aggregator",
    priority: 5,
  },
];

// =============================================================================
// GLOBAL BONUS PORTAL SOURCE (getmybonus.ca)
// =============================================================================

export const GETMYBONUS_SOURCE: SourceConfig = {
  id: "getmybonus-global",
  merchantId: "walmart", // Primary merchant, but used for all
  label: "GetMyBonus Portal",
  url: "https://getmybonus.ca/",
  sourceType: "BONUS_PORTAL",
  scope: "CA",
  official: true,
  priority: 5,
};
